import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sortBy } from 'lodash';
import Box from '../../../../../common/Layout/Box';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import SearchInput from '../../../../../common/SearchInput';
import { t } from '../../../../../../services/i18n';
import { isMobile } from '../../../../../../services/Helpers';
import { Colors } from '../../../../../../styles/Colors';
import { GlobalStyleAttributes } from '../../../../../../styles/GlobalStyles';
import { GlobalConsts } from '../../../../../../styles/GlobalConsts';
import { IconSwitcher } from '../../../../../Icons8Generator/IconSwitcher';
import SelectChips from '../../../../../common/SelectChips';
import { Divider } from 'native-base';
import {
    AUTO_ACTION_CATEGORY,
    AUTO_ACTION_TRIGGERS,
    getAutoActionCategory,
    getAutoActionCategorySelectOptions,
    getAutoActionDescription,
    getAutoActionTitle,
    getOrderedAutoActionCategories,
    getSessionTypeForTrigger,
} from '../../../../../../../web/src/components/AutoActions/AutoActionsConfig';
import AutoActionsEmptyState from '../../../../../../../web/src/components/AutoActions/components/AutoActionsEmptyState';
import AppText from '../../../../../common/AppText';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import usePermissions from '../../../../../../services/Permissions';
import { getSessionAutoMessageInitialValues } from '../StepOptions';

const AutoActionsSelect = (props) => {
    const { initProps, values, setValues, setFieldValue } = props;
    const segment = initProps?.segment;
    const [search, setSearch] = useState('');
    // Kept on the form values so the picked category survives going back from the message step.
    const category = values?.autoActionCategory ?? 'all';
    const setCategory = (value) => setFieldValue('autoActionCategory', value);
    const { has } = usePermissions();

    const categoryOptions = useMemo(() => getAutoActionCategorySelectOptions(segment, { includeIcons: !isMobile }), [segment, isMobile]);

    const triggers = useMemo(() => {
        const categoryOrder = getOrderedAutoActionCategories(segment);
        const catalog = Object.keys(AUTO_ACTION_TRIGGERS).filter((name) => {
            const trigger = AUTO_ACTION_TRIGGERS[name];
            return trigger?.copyKey && trigger?.offerInPicker !== false && (!trigger?.permission || has(trigger.permission));
        }).map((name) => {
            const catalogRow = { action: name };
            const category = getAutoActionCategory(catalogRow);
            const categoryIconName = AUTO_ACTION_CATEGORY[category]?.icon;
            return {
                name,
                title: getAutoActionTitle(catalogRow),
                description: getAutoActionDescription(catalogRow),
                category,
                categoryIcon: categoryIconName ? IconSwitcher(GlobalConsts.iconPrefix.REGULAR, categoryIconName) : null,
                selectedCategoryIcon: categoryIconName ? IconSwitcher(GlobalConsts.iconPrefix.SOLID, categoryIconName) : null,
            };
        });
        return sortBy(catalog, [(trigger) => categoryOrder.indexOf(trigger.category), (trigger) => trigger.title?.toLowerCase()]);
    }, [segment, has]);

    const visibleTriggers = useMemo(() => {
        const searchTerm = search.trim().toLowerCase();
        return triggers.filter((trigger) =>
            (category === 'all' || trigger.category === category) &&
            `${trigger.title} ${trigger.description}`.toLowerCase().includes(searchTerm));
    }, [triggers, search, category]);

    const handleClearFilters = () => {
        setSearch('');
        setCategory('all');
    };

    const onTriggerClick = (trigger) => {
        const messageType = AUTO_ACTION_TRIGGERS[trigger.name]?.action ?? trigger.name;
        const sessionType = getSessionTypeForTrigger(trigger.name);
        const sessionFields = getSessionAutoMessageInitialValues({
            target: [],
            initProps: { messageType, sessionType },
        });

        setValues(prevValues => ({ ...prevValues, messageType, sessionType, ...sessionFields }));
    };

    const selectedMessageType = values?.messageType;

    return (
        <VStack space="0.75rem" width="100%" style={{ paddingInlineEnd: '5px', boxSizing: 'border-box' }}>
            <VStack space="0.75rem" width="100%" style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: Colors.newDesign.white }}>
                <SearchInput
                    value={search}
                    onChange={(value) => setSearch(value ?? '')}
                    placeholder={t('autoActionSearchTriggersPlaceholder')}
                    className="auto-actions-search-input"/>
                <HStack flexWrap="nowrap" overflowX="auto" width="100%">
                    <SelectChips options={categoryOptions} selectedValue={category} onChange={setCategory} testIDPrefix="auto-action-category-chip" containerStyle={{ padding: 0 }}/>
                </HStack>
                <Divider backgroundColor={Colors.borderColor} />
            </VStack>
            <Box width="100%">
                {visibleTriggers.length === 0 ? (
                    <AutoActionsEmptyState variant={search.trim() ? 'search' : 'category'} categoryId={category} onResetFilters={handleClearFilters}/>
                ) : (
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: '0.75rem',
                        width: '100%',
                    }}>
                    {visibleTriggers.map((trigger) => {
                        const { title, description, categoryIcon, selectedCategoryIcon } = trigger;
                        const isSelected = selectedMessageType === (AUTO_ACTION_TRIGGERS[trigger.name]?.action ?? trigger.name) && (values?.sessionType ?? null) === getSessionTypeForTrigger(trigger.name);
                        return (
                            <CustomButtonV2
                                key={trigger.name}
                                onClick={() => onTriggerClick(trigger)}
                                additionalClassName="no-padding"
                                wrapperStyle={{ width: '100%', minWidth: 0 }}
                                override={({ isHovered }) => (
                                    <VStack
                                        space="0.35rem"
                                        flex={1}
                                        style={{
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            borderColor: isSelected ? Colors.newDesign.mainBlue : (isHovered ? Colors.newDesign.darkGrey : Colors.borderColor),
                                            padding: isMobile ? '12px 16px' : '16px',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            minWidth: 0,
                                            width: '100%',
                                            textAlign: 'start',
                                        }}>
                                        <HStack alignItems="center" space="0.5rem" style={{ minWidth: 0 }}>
                                            {categoryIcon && <FontAwesomeIcon icon={isSelected ? selectedCategoryIcon : categoryIcon} width={GlobalConsts.iconSize.XS} height={GlobalConsts.iconSize.XS} color={(isSelected && categoryOptions.find((option) => option.value === trigger.category)?.selectedIconColor) || Colors.newDesign.mainText} />}
                                            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} mediumFont numberOfLines={2} style={{ flex: 1, minWidth: 0 }}>
                                                {title}
                                            </AppText>
                                        </HStack>
                                        {!!description && (
                                            <AppText
                                                fontSize={GlobalStyleAttributes.fonts.heading4}
                                                color={Colors.newDesign.darkGrey}
                                                numberOfLines={3}>
                                                {description}
                                            </AppText>
                                        )}
                                    </VStack>
                                )}/>
                        );
                    })}
                </Box>
                )}
            </Box>
        </VStack>
    );
};

export default AutoActionsSelect;